import React, { useRef, useState, useMemo } from 'react';
import { ArrowLeft, MapPin, Camera, X, Filter, Calendar, Award } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useVirtualizer } from '@tanstack/react-virtual';
import L from 'leaflet';
import type { VergeCondition } from './types';

interface UserReportsProps {
  reports: VergeCondition[];
  onBack: () => void;
  deviceId: string | null;
}

interface PhotoModalProps {
  photoUrl: string;
  onClose: () => void;
}

const PhotoModal = ({ photoUrl, onClose }: PhotoModalProps) => (
  <div className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4">
    <div className="relative max-h-[90vh] max-w-[90vw] w-full flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute -top-4 -right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-[10000]"
        aria-label="Close"
      >
        <X className="w-6 h-6 text-gray-800" />
      </button>
      <img 
        src={photoUrl} 
        alt="Verge condition" 
        className="max-h-[90vh] max-w-[90vw] w-auto h-auto object-contain rounded-lg"
      />
    </div>
  </div>
);

const gradeColors = {
  A: 'bg-green-500',
  B: 'bg-yellow-500',
  C: 'bg-orange-500',
  D: 'bg-red-500'
};

const markerIconCache = new Map<string, L.Icon>();

const getMarkerColor = (grade: 'A' | 'B' | 'C' | 'D') => {
  if (markerIconCache.has(grade)) {
    return markerIconCache.get(grade)!;
  }

  const colors = {
    A: 'green',
    B: 'yellow',
    C: 'orange',
    D: 'red'
  };

  const icon = new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${colors[grade]}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  markerIconCache.set(grade, icon);
  return icon;
};

const createClusterCustomIcon = (cluster: any) => {
  const markers = cluster.getAllChildMarkers();
  const grades = markers.map((m: any) => m.options.grade);

  const hasD = grades.includes('D');
  const hasC = grades.includes('C');
  const hasB = grades.includes('B');

  let color = '#10b981';
  if (hasD) color = '#ef4444';
  else if (hasC) color = '#f97316';
  else if (hasB) color = '#eab308';

  return L.divIcon({
    html: `<div style="background-color: ${color}; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">${cluster.getChildCount()}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(40, 40, true)
  });
};

export function UserReports({ reports, onBack, deviceId }: UserReportsProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGrades, setSelectedGrades] = useState<Set<'A' | 'B' | 'C' | 'D'>>(new Set(['A', 'B', 'C', 'D']));
  const [dateFilter, setDateFilter] = useState<'all' | '7days' | '30days' | '90days'>('all');
  const [selectedAuthority, setSelectedAuthority] = useState<string>('all');
  const parentRef = useRef<HTMLDivElement>(null);

  const authorities = useMemo(() => {
    const authSet = new Set<string>();
    reports.forEach(report => {
      if (report.local_authority) authSet.add(report.local_authority);
    });
    return Array.from(authSet).sort();
  }, [reports]);

  const filteredReports = useMemo(() => {
    let filtered = deviceId ? reports.filter(report => report.device_id === deviceId) : reports;

    filtered = filtered.filter(report => selectedGrades.has(report.grade));

    if (dateFilter !== 'all') {
      const now = new Date();
      const cutoffDays = dateFilter === '7days' ? 7 : dateFilter === '30days' ? 30 : 90;
      const cutoffDate = new Date(now.getTime() - cutoffDays * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(report => new Date(report.timestamp) >= cutoffDate);
    }

    if (selectedAuthority !== 'all') {
      filtered = filtered.filter(report => report.local_authority === selectedAuthority);
    }

    return filtered;
  }, [reports, deviceId, selectedGrades, dateFilter, selectedAuthority]);

  const displayReports = filteredReports;
  const center = displayReports.length > 0
    ? [displayReports[0].latitude, displayReports[0].longitude] as [number, number]
    : [51.505, -0.09] as [number, number];

  const rowVirtualizer = useVirtualizer({
    count: displayReports.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 96,
    overscan: 5
  });

  const toggleGrade = (grade: 'A' | 'B' | 'C' | 'D') => {
    setSelectedGrades(prev => {
      const newSet = new Set(prev);
      if (newSet.has(grade)) {
        newSet.delete(grade);
      } else {
        newSet.add(grade);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        <header className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Back
            </button>
            <h1 className="text-xl font-bold text-gray-800">{deviceId ? 'Your Reports' : 'All Reports'}</h1>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
                showFilters ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {displayReports.length} / {reports.length} reports
            </span>
          </div>
        </header>

        {showFilters && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Award className="w-4 h-4" />
                Grades
              </label>
              <div className="flex gap-2 flex-wrap">
                {(['A', 'B', 'C', 'D'] as const).map(grade => (
                  <button
                    key={grade}
                    onClick={() => toggleGrade(grade)}
                    className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                      selectedGrades.has(grade)
                        ? gradeColors[grade] + ' text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    Grade {grade}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Time Period
              </label>
              <div className="flex gap-2 flex-wrap">
                {[{value: 'all', label: 'All Time'}, {value: '7days', label: 'Last 7 Days'}, {value: '30days', label: 'Last 30 Days'}, {value: '90days', label: 'Last 90 Days'}].map(({value, label}) => (
                  <button
                    key={value}
                    onClick={() => setDateFilter(value as any)}
                    className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                      dateFilter === value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {authorities.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Local Authority
                </label>
                <select
                  value={selectedAuthority}
                  onChange={(e) => setSelectedAuthority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Authorities</option>
                  {authorities.map(auth => (
                    <option key={auth} value={auth}>{auth}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Map */}
        <div className="h-[400px] rounded-lg overflow-hidden shadow-lg mb-4">
          <MapContainer
            center={center}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={createClusterCustomIcon}
              showCoverageOnHover={false}
              spiderfyOnMaxZoom={true}
              maxClusterRadius={50}
            >
              {displayReports.map((report) => (
                <Marker
                  key={report.id}
                  position={[report.latitude, report.longitude]}
                  icon={getMarkerColor(report.grade)}
                  grade={report.grade}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-bold">Grade {report.grade}</p>
                      <p>{report.street_name || 'Unknown road'}</p>
                      {report.local_authority && (
                        <p className="text-gray-600">{report.local_authority}</p>
                      )}
                      <p className="text-gray-500">
                        {new Date(report.timestamp).toLocaleString()}
                      </p>
                      {report.photo_url && (
                        <button
                          onClick={() => setSelectedPhoto(report.photo_url)}
                          className="w-full mt-2"
                        >
                          <img
                            src={report.photo_url}
                            alt="Verge condition"
                            className="rounded w-full hover:opacity-90 transition-opacity"
                          />
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </div>

        {/* Virtualized Reports List */}
        <div 
          ref={parentRef}
          className="h-[500px] overflow-auto rounded-lg bg-gray-50 shadow-inner"
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative'
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const report = displayReports[virtualRow.index];
              return (
                <div
                  key={report.id}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                  className="p-2"
                >
                  <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4 h-20">
                    {/* Grade indicator */}
                    <div className={`${gradeColors[report.grade]} w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}>
                      {report.grade}
                    </div>

                    {/* Text content */}
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center mb-1">
                        <MapPin className="w-4 h-4 text-gray-500 mr-1 flex-shrink-0" />
                        <p className="text-gray-800 font-medium truncate">
                          {report.street_name || 'Unknown road'}
                        </p>
                      </div>
                      {report.local_authority && (
                        <p className="text-gray-600 text-sm mb-1 truncate">{report.local_authority}</p>
                      )}
                      <p className="text-gray-500 text-sm">
                        {new Date(report.timestamp).toLocaleString()}
                      </p>
                    </div>

                    {/* Photo */}
                    <div className="flex-shrink-0 w-28">
                      {report.photo_url ? (
                        <button 
                          onClick={() => setSelectedPhoto(report.photo_url)}
                          className="w-28 h-16 rounded-lg overflow-hidden bg-gray-100 group relative"
                        >
                          <img 
                            src={report.photo_url} 
                            alt="Verge condition" 
                            className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity" />
                        </button>
                      ) : (
                        <div className="w-28 h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Camera className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {displayReports.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No reports found.</p>
          </div>
        )}

        {selectedPhoto && (
          <PhotoModal 
            photoUrl={selectedPhoto} 
            onClose={() => setSelectedPhoto(null)} 
          />
        )}
      </div>
    </div>
  );
}